import * as React from "react";
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { Modal } from "react-bootstrap";
import { apiEndpoint } from "../config/api";
import { apiFetch } from "../services/api";

// Self-contained BDM (Business Development Manager) reference-lookup manager.
// Reads/writes app.lksi_bdm via bdm-list / bdm-create / bdm-update. Deactivate/reactivate
// rather than delete. Kept intentionally simple & functional (polish deferred to Stage 6).
export default function BdmDirectory() {
  const [bdms, setBdms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Add form
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [addError, setAddError] = useState(null);
  const [addSaving, setAddSaving] = useState(false);

  // Edit modal
  const [editBdm, setEditBdm] = useState(null); // { bdm_id, display_name, email, notes }
  const [editError, setEditError] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null); // the bdm pending delete
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  const fetchBdms = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiFetch(`${apiEndpoint}/bdm-list`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load BDMs");
      setBdms(data.bdms || []);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBdms();
  }, []);

  const handleCreate = async () => {
    setAddSaving(true);
    setAddError(null);
    try {
      const res = await apiFetch(`${apiEndpoint}/bdm-create`, {
        method: "POST",
        headers: { "Content-type": "application/json; charset=UTF-8" },
        body: JSON.stringify({
          display_name: newName,
          email: newEmail || null,
          notes: newNotes || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to create BDM");
      setNewName("");
      setNewEmail("");
      setNewNotes("");
      await fetchBdms();
    } catch (e) {
      setAddError(e.message);
    } finally {
      setAddSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    setEditSaving(true);
    setEditError(null);
    try {
      const res = await apiFetch(`${apiEndpoint}/bdm-update`, {
        method: "POST",
        headers: { "Content-type": "application/json; charset=UTF-8" },
        body: JSON.stringify({
          bdm_id: editBdm.bdm_id,
          display_name: editBdm.display_name,
          email: editBdm.email || null,
          notes: editBdm.notes || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update BDM");
      setEditBdm(null);
      await fetchBdms();
    } catch (e) {
      setEditError(e.message);
    } finally {
      setEditSaving(false);
    }
  };

  const handleToggleActive = async (bdm) => {
    setLoadError(null);
    try {
      const res = await apiFetch(`${apiEndpoint}/bdm-update`, {
        method: "POST",
        headers: { "Content-type": "application/json; charset=UTF-8" },
        body: JSON.stringify({ bdm_id: bdm.bdm_id, active: !bdm.active }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update BDM");
      await fetchBdms();
    } catch (e) {
      setLoadError(e.message);
    }
  };

  const handleConfirmDelete = async () => {
    setDeleteSaving(true);
    setDeleteError(null);
    try {
      const res = await apiFetch(`${apiEndpoint}/bdm-delete`, {
        method: "POST",
        headers: { "Content-type": "application/json; charset=UTF-8" },
        body: JSON.stringify({ bdm_id: deleteTarget.bdm_id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete BDM");
      setDeleteTarget(null);
      await fetchBdms();
    } catch (e) {
      setDeleteError(e.message);
    } finally {
      setDeleteSaving(false);
    }
  };

  const bdmRows = bdms.map((bdm) => (
    <TableRow key={bdm.bdm_id}>
      <TableCell>{bdm.display_name}</TableCell>
      <TableCell>{bdm.email || <em>—</em>}</TableCell>
      <TableCell>
        {bdm.active ? (
          <Chip label="Active" color="success" size="small" variant="outlined" />
        ) : (
          <Chip label="Inactive" size="small" variant="outlined" />
        )}
      </TableCell>
      <TableCell>{bdm.notes || <em>—</em>}</TableCell>
      <TableCell align="right">
        <Button
          size="small"
          onClick={() =>
            setEditBdm({
              bdm_id: bdm.bdm_id,
              display_name: bdm.display_name,
              email: bdm.email || "",
              notes: bdm.notes || "",
            })
          }
          sx={{ mr: 1 }}
        >
          Edit
        </Button>
        <Button size="small" color="secondary" onClick={() => handleToggleActive(bdm)} sx={{ mr: 1 }}>
          {bdm.active ? "Deactivate" : "Reactivate"}
        </Button>
        <Button
          size="small"
          color="error"
          onClick={() => {
            setDeleteError(null);
            setDeleteTarget(bdm);
          }}
        >
          Delete
        </Button>
      </TableCell>
    </TableRow>
  ));

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" component="h2" fontWeight={600} sx={{ mb: 0.5 }}>
        BDM Directory
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Business Development Managers used for facility ownership assignments. Deactivate rather
        than delete; existing assignments to an inactive BDM keep resolving.
      </Typography>

      {/* Add BDM */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 1 }}>
        <TextField
          label="Display name"
          size="small"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <TextField
          label="Email (optional)"
          size="small"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
        />
        <TextField
          label="Notes (optional)"
          size="small"
          value={newNotes}
          onChange={(e) => setNewNotes(e.target.value)}
        />
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={addSaving || !newName.trim()}
        >
          {addSaving ? "Adding…" : "Add BDM"}
        </Button>
      </Box>
      {addError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {addError}
        </Typography>
      )}

      {loadError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {loadError}
        </Typography>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">
                  Loading…
                </Typography>
              </TableCell>
            </TableRow>
          ) : bdms.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">
                  No BDMs yet.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            bdmRows
          )}
        </TableBody>
      </Table>

      {/* Edit modal */}
      <Modal show={!!editBdm} onHide={() => setEditBdm(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit BDM</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editBdm && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField
                label="Display name"
                size="small"
                fullWidth
                value={editBdm.display_name}
                onChange={(e) => setEditBdm({ ...editBdm, display_name: e.target.value })}
              />
              <TextField
                label="Email"
                size="small"
                fullWidth
                value={editBdm.email}
                onChange={(e) => setEditBdm({ ...editBdm, email: e.target.value })}
              />
              <TextField
                label="Notes"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={editBdm.notes}
                onChange={(e) => setEditBdm({ ...editBdm, notes: e.target.value })}
              />
              {editError && (
                <Typography variant="body2" color="error">
                  {editError}
                </Typography>
              )}
            </Box>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setEditBdm(null)} color="secondary">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={editSaving || !(editBdm && editBdm.display_name.trim())}
          >
            {editSaving ? "Saving…" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete confirmation */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete BDM</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1 }}>
              <Typography variant="body2">
                Permanently delete <strong>{deleteTarget.display_name}</strong>? This cannot be
                undone. Deletion is blocked if the BDM is assigned to any facility.
              </Typography>
              {deleteError && (
                <Typography variant="body2" color="error">
                  {deleteError}
                </Typography>
              )}
            </Box>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setDeleteTarget(null)} color="secondary">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteSaving}
          >
            {deleteSaving ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Paper>
  );
}
